import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { heeftRol } from "@/lib/rollen";
import TourIntro from "./TourIntro";
import TourBallon from "./TourBallon";
import { meldGebruik, zichtbaarElement } from "./rondleidingApi";
import { vervaagdeSchermafbeelding } from "./schermafbeelding";

/** Rondleiding over het huidige scherm: uitlegstappen (zonder knop) altijd, knopstappen alleen als de knop voor deze gebruiker zichtbaar is (en bij zijn rol hoort). */
export default function RondleidingTour({ scherm, user, directie, onSluit }) {
  const nav = useNavigate();
  const [idx, setIdx] = useState(-1);
  const [rect, setRect] = useState(null);
  const [bezigBeeld, setBezigBeeld] = useState(false);
  const klaar = useRef(false);
  const { stappen, overgeslagen, gevonden, niet } = useMemo(() => {
    const perRol = (scherm.stappen || []).filter((s) => !(s.rollen || []).length || heeftRol(user, s.rollen));
    const zicht = perRol.filter((s) => !s.selector || zichtbaarElement(s.selector));
    return { stappen: zicht, overgeslagen: (scherm.stappen || []).length - zicht.length, gevonden: zicht.filter((s) => s.selector).map((s) => s.id),
             niet: perRol.filter((s) => !zicht.includes(s)).map((s) => s.id) };
  }, [scherm, user]);
  useEffect(() => {
    api.post("/rondleiding/melding", { scherm_id: scherm.id, gevonden, niet_gevonden: niet }).catch(() => {});
    meldGebruik(scherm, "geopend", { totaal: stappen.length });
  }, [scherm, gevonden, niet, stappen.length]);
  const stap = idx >= 0 ? stappen[idx] : null;
  const sluit = useCallback(() => {
    if (!klaar.current) meldGebruik(scherm, "gesloten", { stap_nr: idx + 1, totaal: stappen.length, stap_titel: stap?.titel || "" });
    onSluit();
  }, [scherm, idx, stappen.length, stap, onSluit]);
  const meet = useCallback(() => { const el = stap?.selector && zichtbaarElement(stap.selector); setRect(el ? el.getBoundingClientRect() : null); }, [stap]);
  useEffect(() => {
    if (!stap) return undefined;
    meldGebruik(scherm, "stap", { stap_nr: idx + 1, totaal: stappen.length });
    const el = stap.selector && zichtbaarElement(stap.selector);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(meet, el ? 350 : 0);
    window.addEventListener("resize", meet);
    window.addEventListener("scroll", meet, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", meet); window.removeEventListener("scroll", meet, true); };
  }, [stap, meet, scherm, idx, stappen.length]);
  const volgende = useCallback(() => {
    if (idx + 1 < stappen.length) { setIdx(idx + 1); return; }
    klaar.current = true;
    meldGebruik(scherm, "afgerond", { stap_nr: stappen.length, totaal: stappen.length });
    onSluit();
  }, [idx, stappen.length, scherm, onSluit]);
  const start = () => { meldGebruik(scherm, "gestart", { totaal: stappen.length }); setIdx(0); };
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") sluit(); else if (e.key === "ArrowRight" && idx >= 0) volgende(); else if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [idx, volgende, sluit]);
  const beeld = async () => {
    setBezigBeeld(true);
    try {
      const blob = await vervaagdeSchermafbeelding();
      const fd = new FormData();
      fd.append("bestand", blob, "scherm.png");
      await api.post(`/rondleiding/schermen/${scherm.id}/schermafbeelding`, fd);
      toast.success("Schermafbeelding (vervaagd) bewaard voor het PDF-handboek");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Schermafbeelding mislukt"); }
    setBezigBeeld(false);
  };
  if (!stap) {
    return <TourIntro scherm={scherm} aantal={stappen.length} overgeslagen={overgeslagen} directie={directie} bezigBeeld={bezigBeeld} onStart={start}
      onSluit={sluit} onBeeld={beeld} onBewerk={() => { onSluit(); nav(`/rondleiding-beheer?scherm=${scherm.id}`); }} />;
  }
  return <TourBallon rect={rect} stap={stap} nr={idx + 1} totaal={stappen.length} onVorige={() => setIdx(Math.max(0, idx - 1))} onVolgende={volgende} onSluit={sluit} />;
}
